import {
  MobType, VillagerState, VillagerJob, MOB_SPEEDS,
  VILLAGER_SCHEDULES,
} from './types'
import type { MobData, VillageSimData, ScheduleEntry } from './types'

const ARRIVAL_THRESHOLD = 0.0003  // ~3 units — close enough to target

// ─── Get current schedule entry for a villager ──────────────────────
function getCurrentSchedule(job: number, gameHour: number): ScheduleEntry {
  const schedule = VILLAGER_SCHEDULES[job] ?? VILLAGER_SCHEDULES[VillagerJob.Farmer]
  let current = schedule[schedule.length - 1]
  for (const entry of schedule) {
    if (gameHour >= entry.hour) current = entry
  }
  return current
}

// ─── Convert local village coords to sphere normal ──────────────────
function localToNormal(
  village: VillageSimData,
  localX: number, localZ: number,
): { nx: number; ny: number; nz: number } {
  const wx = village.position.x + village.tangentX.x * localX + village.tangentZ.x * localZ
  const wy = village.position.y + village.tangentX.y * localX + village.tangentZ.y * localZ
  const wz = village.position.z + village.tangentX.z * localX + village.tangentZ.z * localZ
  const len = Math.sqrt(wx * wx + wy * wy + wz * wz)
  return { nx: wx / len, ny: wy / len, nz: wz / len }
}

// ─── Find closest structure of a given type in the village ──────────
function findStructure(
  village: VillageSimData,
  structureType: string,
  fromNx: number, fromNy: number, fromNz: number,
): { nx: number; ny: number; nz: number } | null {
  let best: { nx: number; ny: number; nz: number } | null = null
  let bestDist = Infinity

  for (const s of village.structures) {
    if (s.type !== structureType) continue
    const target = localToNormal(village, s.localX, s.localZ)
    const dot = fromNx * target.nx + fromNy * target.ny + fromNz * target.nz
    const dist = Math.acos(Math.min(1, Math.max(-1, dot)))
    if (dist < bestDist) {
      bestDist = dist
      best = target
    }
  }

  // Fallback: if target structure type not in village, try house
  if (!best && structureType !== 'house') {
    return findStructure(village, 'house', fromNx, fromNy, fromNz)
  }

  return best
}

// ─── Tangent frame ──────────────────────────────────────────────────
function tangentFrame(nx: number, ny: number, nz: number) {
  let ex: number, ey: number, ez: number
  if (Math.abs(ny) < 0.9) {
    ex = -nz; ey = 0; ez = nx
  } else {
    ex = 0; ey = nz; ez = -ny
  }
  const el = Math.sqrt(ex * ex + ey * ey + ez * ez)
  ex /= el; ey /= el; ez /= el
  const fx = ny * ez - nz * ey
  const fy = nz * ex - nx * ez
  const fz = nx * ey - ny * ex
  return { ex, ey, ez, fx, fy, fz }
}

// ─── Steer toward a target (set heading toward targetNx/Ny/Nz) ─────
function steerTowardTarget(mob: MobData) {
  if (!mob.hasTarget) return

  const dot = mob.nx * mob.targetNx + mob.ny * mob.targetNy + mob.nz * mob.targetNz
  const dist = Math.acos(Math.min(1, Math.max(-1, dot)))

  if (dist < ARRIVAL_THRESHOLD) {
    // Arrived
    mob.hasTarget = false
    mob.speed = 0
    mob.animSpeed = 0
    return
  }

  // Compute angle toward target in tangent plane
  const { ex, ey, ez, fx, fy, fz } = tangentFrame(mob.nx, mob.ny, mob.nz)
  const dx = mob.targetNx - mob.nx
  const dy = mob.targetNy - mob.ny
  const dz = mob.targetNz - mob.nz
  const projE = dx * ex + dy * ey + dz * ez
  const projF = dx * fx + dy * fy + dz * fz
  const targetAngle = Math.atan2(projE, projF)

  // Smooth turn toward target
  let diff = targetAngle - mob.heading
  while (diff > Math.PI) diff -= Math.PI * 2
  while (diff < -Math.PI) diff += Math.PI * 2
  mob.heading += diff * 0.2

  mob.speed = MOB_SPEEDS[MobType.Villager].wander
  mob.animSpeed = 1
}

// ─── Main villager AI tick ──────────────────────────────────────────
export function updateVillager(
  mob: MobData,
  gameHour: number,
  villages: VillageSimData[],
  tickSeed: number,
) {
  const village = villages[mob.villageIndex]
  if (!village) return

  const schedule = getCurrentSchedule(mob.subType, gameHour)

  switch (schedule.state) {
    case VillagerState.Sleep: {
      mob.speed = 0
      mob.animSpeed = 0
      mob.hasTarget = false
      break
    }

    case VillagerState.Walk: {
      // Walk toward target structure
      if (!mob.hasTarget && schedule.targetStructure) {
        const target = findStructure(village, schedule.targetStructure, mob.nx, mob.ny, mob.nz)
        if (target) {
          mob.targetNx = target.nx
          mob.targetNy = target.ny
          mob.targetNz = target.nz
          mob.hasTarget = true
        }
      }
      steerTowardTarget(mob)
      break
    }

    case VillagerState.Working: {
      mob.speed = 0
      mob.animSpeed = 0.2  // subtle working animation
      mob.hasTarget = false
      break
    }

    case VillagerState.Socializing: {
      // Slight idle movement near pub
      mob.speed = 0.3
      mob.animSpeed = 0.15
      // Small random drift
      mob.stateTimer -= 0.25
      if (mob.stateTimer <= 0) {
        mob.heading += (((tickSeed * 73856093 + mob.villageIndex * 19349663) >>> 0) / 4294967296 - 0.5) * 0.8
        mob.stateTimer = 1 + ((tickSeed * 2246822507 + mob.villageIndex * 340573321) >>> 0) / 4294967296 * 2
      }
      break
    }

    case VillagerState.Idle: {
      mob.speed = 0
      mob.animSpeed = 0
      mob.hasTarget = false
      // Occasional idle turn
      mob.stateTimer -= 0.25
      if (mob.stateTimer <= 0) {
        mob.heading += (((tickSeed * 12345 + mob.villageIndex * 67890) >>> 0) / 4294967296 - 0.5) * 0.5
        mob.stateTimer = 2 + ((tickSeed * 54321 + mob.villageIndex * 98765) >>> 0) / 4294967296 * 3
      }
      break
    }

    case VillagerState.Patrol: {
      // Guard patrols: walk in a loop around the village center
      mob.speed = MOB_SPEEDS[MobType.Villager].wander * 0.7
      mob.animSpeed = 0.8
      // Slowly orbit around village center
      const { ex, ey, ez, fx, fy, fz } = tangentFrame(mob.nx, mob.ny, mob.nz)
      const toVillageX = village.normal.x - mob.nx
      const toVillageY = village.normal.y - mob.ny
      const toVillageZ = village.normal.z - mob.nz
      const projE = toVillageX * ex + toVillageY * ey + toVillageZ * ez
      const projF = toVillageX * fx + toVillageY * fy + toVillageZ * fz
      const toCenter = Math.atan2(projE, projF)
      // Perpendicular to center = orbit
      mob.heading = toCenter + Math.PI * 0.5
      // Keep roughly constant distance from center
      const dot = mob.nx * village.normal.x + mob.ny * village.normal.y + mob.nz * village.normal.z
      const dist = Math.acos(Math.min(1, Math.max(-1, dot)))
      const targetDist = 0.004  // ~40 units from center
      if (dist > targetDist * 1.3) {
        mob.heading = toCenter  // too far, steer inward
      } else if (dist < targetDist * 0.7) {
        mob.heading = toCenter + Math.PI  // too close, steer outward
      }
      break
    }
  }
}
